// Tasks: the gateway's task ledger — filter chips, the table, and the per-task
// drawer with its phase timeline, input arguments, live result and log tail.
//
// This file used to be "Activity", a tab bar over six unrelated lists that all
// happened to be live. Each of the other five has gone to the page that answers
// its own question: the audit feed and the call log to Logs, the usage ledger to
// Audit, the queue depths to their own panel under the Overview graph, and the
// live streams to Streams, next to the routes that produce them.
//
// What is left is one list, drawn in exactly ONE place (UX19): a panel on Overview,
// under the queues that explain why a task is waiting. It is not a nav row and not a
// page of its own — /tasks redirects to Overview — because "what is running" is a
// reading of how things are going, not a separate destination.

import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api/client';
import { useResource } from '../lib/useResource';
import { useLiveReseed, tasksSig } from '../lib/useLiveReseed';
import { useNames } from '../lib/useNames';
import { useStore } from '../store/store';
import {
  scopeTasks,
  DEFAULT_TASK_SCOPE,
  TASK_SCOPES,
  TASK_SCOPE_LABEL,
  TASK_SCOPE_TITLE,
  isTaskScope,
  taskListScope,
  taskNetworkFilter,
  type TaskScope,
} from '../lib/networkScope';
import { useAuth } from '../auth/AuthContext';
import { Loading, ErrorState, StatusBadge, Badge, JsonView, Segmented } from '../components/ui';
import { DataTable, type Column } from '../components/DataTable';
import { ConfirmDialog } from '../components/Modal';
import { Drawer, DetailRow } from '../components/Drawer';
import { friendly } from './Networks';
import type { Task } from '../api/schemas';

// ST42 — the task-state filter vocabulary rendered as chips.
export const TASK_STATES = ['QUEUED', 'DISPATCHED', 'RUNNING', 'SUCCEEDED', 'FAILED', 'CANCELLED'] as const;

// ST42 — the four phase timestamps of a task, in order (absent phases dropped).
export function taskPhases(task: Pick<Task, 'queued_at' | 'dispatched_at' | 'started_at' | 'finished_at'>): { label: string; ts: string }[] {
  const out: { label: string; ts: string }[] = [];
  if (task.queued_at) out.push({ label: 'Queued', ts: task.queued_at });
  if (task.dispatched_at) out.push({ label: 'Dispatched', ts: task.dispatched_at });
  if (task.started_at) out.push({ label: 'Started', ts: task.started_at });
  if (task.finished_at) out.push({ label: 'Finished', ts: task.finished_at });
  return out;
}

// ST42 — per-phase durations in ms. Prefer the gateway-supplied `durations_ms`, else
// derive them from the phase timestamps.
export function taskDurations(
  task: Pick<Task, 'queued_at' | 'dispatched_at' | 'started_at' | 'finished_at' | 'durations_ms'>,
): { queue?: number; dispatch?: number; run?: number; total?: number } {
  if (task.durations_ms && Object.keys(task.durations_ms).length) return task.durations_ms;
  const ms = (t?: string) => (t ? new Date(t).getTime() : undefined);
  const q = ms(task.queued_at);
  const d = ms(task.dispatched_at);
  const s = ms(task.started_at);
  const f = ms(task.finished_at);
  const diff = (a?: number, b?: number) => (a != null && b != null && b >= a ? b - a : undefined);
  return { queue: diff(q, d), dispatch: diff(d, s), run: diff(s, f), total: diff(q, f) };
}

function fmtDur(ms?: number): string {
  if (ms == null) return '—';
  if (ms < 1000) return `${Math.round(ms)}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

export function fmtBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

const CANCELLABLE = new Set(['QUEUED', 'DISPATCHED', 'RUNNING']);

// The task's input arguments, sourced from whichever field the gateway populates: `arguments`
// (preferred) or the small inline `arguments_ref` JSON. A bare string `arguments_ref` is an opaque
// payload pointer (large args stored out-of-line), not a JSON object — surfaced separately.
export function taskArguments(
  task: Pick<Task, 'arguments' | 'arguments_ref'>,
): Record<string, unknown> | undefined {
  if (task.arguments && typeof task.arguments === 'object') return task.arguments;
  if (task.arguments_ref && typeof task.arguments_ref === 'object') {
    return task.arguments_ref as Record<string, unknown>;
  }
  return undefined;
}

const HOUR_MS = 3600_000;

// OW1 — a member's «Мои» / «Сети» choice is a per-browser convenience; it survives a reload.
const TASK_SCOPE_KEY = 'overview.tasks.scope';
function readTaskScope(): TaskScope {
  try {
    const v = typeof localStorage !== 'undefined' ? localStorage.getItem(TASK_SCOPE_KEY) : null;
    return isTaskScope(v) ? v : DEFAULT_TASK_SCOPE;
  } catch {
    return DEFAULT_TASK_SCOPE;
  }
}
function writeTaskScope(v: TaskScope): void {
  try {
    localStorage?.setItem(TASK_SCOPE_KEY, v);
  } catch {
    /* private window / blocked storage: the choice just doesn't persist */
  }
}

/**
 * `networkIds` — the Overview scope (UX18/§18), the same set the graph and the queues take.
 * Undefined/null means "no opinion" (the network list has not loaded yet).
 *
 * A member has no control to move it: on «Сети» the gateway returns only the tasks of the networks
 * they belong to, and `mine` is exactly that set. An admin, whose list IS the org's, gets the
 * segmented control — and the panel must answer to it, or the page has a graph scoped to five
 * networks sitting above a table of six hundred.
 *
 * OW1 — a member instead gets a «Мои» / «Сети» control over WHOSE tasks: «Мои» (default) asks the
 * gateway for `scope=own` — their own calls, the implicit platform network included — and skips the
 * network filter; «Сети» is the member-network ledger as before.
 */
export function TasksPanel({ networkIds }: { networkIds?: Set<string> | null }) {
  const { isAdmin, user } = useAuth();
  // OW1 — a member chooses whose tasks: «Мои» (own calls, any network of the org — the platform
  // network included) or «Сети» (the member-network ledger). An admin sends no scope: unchanged.
  const [taskScope, setTaskScopeState] = useState<TaskScope>(readTaskScope);
  const setTaskScope = (v: TaskScope) => {
    setTaskScopeState(v);
    writeTaskScope(v);
  };
  const listScope = taskListScope({ isAdmin, signedIn: !!user }, taskScope);
  // ST42 — state + tool filter chips, plus a "failed in the last hour" preset.
  const [stateFilter, setStateFilter] = useState<string>('');
  const [toolFilter, setToolFilter] = useState<string>('');
  const [failedHour, setFailedHour] = useState(false);
  const effState = failedHour ? 'FAILED' : stateFilter;
  // Cancel-all confirm + in-flight state.
  const [confirmAll, setConfirmAll] = useState(false);
  const [busyAll, setBusyAll] = useState(false);
  const [errAll, setErrAll] = useState<string | null>(null);

  const res = useResource(
    () => api.tasks({ state: effState || undefined, tool: toolFilter || undefined, scope: listScope }),
    [effState, toolFilter, listScope],
  );
  // Realtime: re-fetch whenever a task.updated frame changes the live task set. Only the FIRST page
  // is re-fetched; pages appended with «Загрузить ещё» are kept and merged under it by id.
  useLiveReseed(res.reload, tasksSig);
  const [selected, setSelected] = useState<Task | null>(null);
  // Pages appended past the first one, and the cursor of the page after them. Reset whenever the
  // filters change — a cursor belongs to the query that produced it.
  const [extra, setExtra] = useState<Task[]>([]);
  const [extraCursor, setExtraCursor] = useState<string | null | undefined>(undefined);
  const [moreBusy, setMoreBusy] = useState(false);
  const [moreErr, setMoreErr] = useState<string | null>(null);
  useEffect(() => {
    setExtra([]);
    setExtraCursor(undefined);
    setMoreErr(null);
  }, [effState, toolFilter, listScope]);
  // undefined = nothing appended yet → the first page's cursor decides.
  const nextCursor = extraCursor === undefined ? (res.data?.nextCursor ?? null) : extraCursor;
  const loaded = useMemo(() => {
    const first = res.data?.items ?? [];
    const seen = new Set(first.map((t) => t.id));
    return [...first, ...extra.filter((t) => !seen.has(t.id))];
  }, [res.data, extra]);

  async function loadMore() {
    if (!nextCursor) return;
    setMoreBusy(true);
    setMoreErr(null);
    try {
      const page = await api.tasks({ state: effState || undefined, tool: toolFilter || undefined, scope: listScope, cursor: nextCursor });
      setExtra((prev) => [...prev, ...page.items]);
      setExtraCursor(page.nextCursor);
    } catch (e) {
      setMoreErr(friendly(e));
    } finally {
      setMoreBusy(false);
    }
  }

  // The networks in scope come first: every other number on this panel — the tool dropdown, the
  // in-flight count behind "Cancel all" — is about what the reader is looking at, not about what
  // the gateway happened to return.
  // «Мои» rows are the caller's own calls and include networks outside their membership on purpose,
  // so the network scope steps aside for them (taskNetworkFilter).
  const inScope = useMemo(
    () => scopeTasks(loaded, taskNetworkFilter(listScope, networkIds ?? null)),
    [loaded, networkIds, listScope],
  );

  const rows = useMemo(() => {
    if (!failedHour) return inScope;
    const cutoff = Date.now() - HOUR_MS;
    return inScope.filter((t) => {
      const f = t.finished_at ? new Date(t.finished_at).getTime() : 0;
      return f >= cutoff;
    });
  }, [inScope, failedHour]);

  // Distinct tools seen (for the tool filter dropdown).
  const tools = useMemo(() => {
    const set = new Set<string>(inScope.map((t) => t.canonical_tool));
    if (toolFilter) set.add(toolFilter);
    return [...set].sort();
  }, [inScope, toolFilter]);

  const cols: Column<Task>[] = [
    { key: 'tool', header: 'Tool', cell: (t) => <code className="mono">{t.canonical_tool}</code>, sortValue: (t) => t.canonical_tool },
    { key: 'state', header: 'State', cell: (t) => <StatusBadge status={t.state} />, sortValue: (t) => t.state },
    { key: 'prio', header: 'Priority', align: 'right', cell: (t) => t.priority, sortValue: (t) => t.priority },
    { key: 'async', header: 'Async', cell: (t) => (t.is_async ? <Badge tone="info">async</Badge> : <Badge tone="muted">sync</Badge>) },
    { key: 'queued', header: 'Queued', cell: (t) => (t.queued_at ? new Date(t.queued_at).toLocaleString() : '—'), sortValue: (t) => t.queued_at ?? '' },
  ];

  function pickState(s: string) {
    setFailedHour(false);
    setStateFilter((prev) => (prev === s ? '' : s));
  }

  const IN_FLIGHT = new Set(['QUEUED', 'DISPATCHED', 'RUNNING']);
  const inFlight = inScope.filter((t) => IN_FLIGHT.has(t.state)).length;

  async function cancelAll() {
    setBusyAll(true);
    setErrAll(null);
    try {
      await api.cancelAllTasks();
      setConfirmAll(false);
      res.reload();
    } catch (e) {
      setErrAll(friendly(e));
    } finally {
      setBusyAll(false);
    }
  }

  return (
    <>
      <div className="filter-bar" style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center', marginBottom: 8 }}>
        {/* OW1 — members only: an admin's list is the org, narrowed by the Networks control above. */}
        {user && !isAdmin && (
          <div data-testid="tasks-scope">
            <Segmented<TaskScope>
              value={taskScope}
              onChange={setTaskScope}
              options={TASK_SCOPES.map((v) => ({
                value: v,
                label: <span title={TASK_SCOPE_TITLE[v]}>{TASK_SCOPE_LABEL[v]}</span>,
              }))}
            />
          </div>
        )}
        {TASK_STATES.map((s) => (
          <button
            key={s}
            className={`chip filter-chip ${!failedHour && stateFilter === s ? 'active' : ''}`}
            aria-pressed={!failedHour && stateFilter === s}
            onClick={() => pickState(s)}
          >
            {s}
          </button>
        ))}
        <button
          className={`chip filter-chip ${failedHour ? 'active' : ''}`}
          aria-pressed={failedHour}
          onClick={() => { setFailedHour((v) => !v); setStateFilter(''); }}
          title="Tasks that failed in the last hour"
        >
          ⚠ Failed (1h)
        </button>
        <select className="filter-tool" value={toolFilter} onChange={(e) => setToolFilter(e.target.value)} aria-label="Filter by tool">
          <option value="">all tools</option>
          {tools.map((t) => (
            <option key={t} value={t}>{t}</option>
          ))}
        </select>
        {(stateFilter || toolFilter || failedHour) && (
          <button className="btn btn-sm" onClick={() => { setStateFilter(''); setToolFilter(''); setFailedHour(false); }}>Clear</button>
        )}
        {/* Cancel-all destroys work queued by everyone else in a shared network, so the
            gateway made it admin-only (BF28 fix-round-1). Members see the task list — which
            is scoped to them — without a button that would only ever 403. */}
        {isAdmin && (
          <button
            className="btn btn-sm btn-danger"
            data-testid="tasks-cancel-all"
            style={{ marginLeft: 'auto' }}
            disabled={inFlight === 0}
            title={inFlight === 0 ? 'No in-flight tasks' : 'Cancel every queued/dispatched/running task'}
            onClick={() => { setErrAll(null); setConfirmAll(true); }}
          >
            Cancel all{inFlight > 0 ? ` (${inFlight})` : ''}
          </button>
        )}
      </div>
      {confirmAll && (
        <ConfirmDialog
          title="Cancel all tasks"
          message={<>Cancel every in-flight task in the organization (queued, dispatched, running)? The gateway does not take the network scope above — this reaches beyond the {inFlight} shown here. Tasks already finished are not affected.</>}
          confirmLabel="Cancel all"
          busy={busyAll}
          error={errAll}
          onConfirm={cancelAll}
          onClose={() => setConfirmAll(false)}
        />
      )}
      {res.loading && !res.data ? (
        <Loading />
      ) : res.error ? (
        <ErrorState message={res.error} onRetry={res.reload} />
      ) : (
        <>
          <DataTable columns={cols} rows={rows} rowKey={(t) => t.id} empty="No tasks match." onRowClick={(t) => setSelected(t)} />
          {nextCursor && (
            <div style={{ display: 'flex', justifyContent: 'center', marginTop: 8 }}>
              <button className="btn btn-sm" data-testid="tasks-load-more" disabled={moreBusy} onClick={loadMore}>
                {moreBusy ? 'Загрузка…' : 'Загрузить ещё'}
              </button>
            </div>
          )}
          {moreErr && <ErrorState message={moreErr} onRetry={loadMore} />}
        </>
      )}
      {selected && (
        <TaskDrawer
          task={selected}
          onClose={() => setSelected(null)}
          onChanged={(next) => {
            res.reload();
            setSelected(next);
          }}
        />
      )}
    </>
  );
}

function TaskDrawer({ task, onClose, onChanged }: { task: Task; onClose: () => void; onChanged: (next: Task | null) => void }) {
  const names = useNames();
  // ST42 — live outcome from the store (task.completed), so the open drawer updates without a refetch.
  const live = useStore((s) => s.tasks[task.id]);
  const phases = taskPhases(task);
  const durations = taskDurations(task);
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // The list row is LIGHT (no arguments, no result — the live task.updated frame never carries the
  // arguments either), so the drawer fetches the full row (GET /tasks/{id}) on open and shows the
  // Input and Result from THAT. Falls back to whatever the row itself carries if the fetch fails.
  const [full, setFull] = useState<Task | null>(null);
  const [fullLoading, setFullLoading] = useState(true);
  const [args, setArgs] = useState<Record<string, unknown> | undefined>(() => taskArguments(task));
  const [argStr, setArgStr] = useState<string | undefined>(() =>
    typeof task.arguments_ref === 'string' ? task.arguments_ref : undefined,
  );
  useEffect(() => {
    let alive = true;
    setArgs(taskArguments(task));
    setArgStr(typeof task.arguments_ref === 'string' ? task.arguments_ref : undefined);
    setFull(null);
    setFullLoading(true);
    api
      .task(task.id)
      .then((t) => {
        if (!alive) return;
        setFull(t);
        setArgs((prev) => taskArguments(t) ?? prev);
        if (typeof t.arguments_ref === 'string') setArgStr(t.arguments_ref);
      })
      .catch(() => {
        /* keep the row's args; detail is best-effort */
      })
      .finally(() => {
        if (alive) setFullLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [task.id]);

  const resultRef = full?.result_ref ?? task.result_ref;

  async function cancel() {
    setBusy(true);
    setErr(null);
    try {
      const next = await api.cancelTask(task.id);
      onChanged(next);
      setConfirm(false);
    } catch (e) {
      setErr(friendly(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Drawer
      title={<code className="mono">{task.canonical_tool}</code>}
      subtitle={<code className="mono small">{task.id}</code>}
      onClose={onClose}
      actions={
        CANCELLABLE.has(task.state) ? (
          <button className="btn btn-sm btn-danger" onClick={() => setConfirm(true)}>Cancel task</button>
        ) : (
          <span className="muted small">terminal</span>
        )
      }
    >
      {err && <ErrorState message={err} />}
      <DetailRow label="State"><StatusBadge status={live?.state ?? task.state} /></DetailRow>
      <DetailRow label="Priority">{task.priority}</DetailRow>
      <DetailRow label="Async">{task.is_async ? 'yes' : 'no'}</DetailRow>
      {task.network_id && <DetailRow label="Network"><span title={task.network_id}>{names.networkName(task.network_id)}</span></DetailRow>}
      {task.provider_instance_id && <DetailRow label="Provider"><code className="mono small">{task.provider_instance_id}</code></DetailRow>}
      {task.progress_fraction != null && <DetailRow label="Progress">{Math.round(task.progress_fraction * 100)}%</DetailRow>}
      {task.usage && <DetailRow label="GPU·s">{task.usage.gpu_seconds.toFixed(1)}</DetailRow>}
      {(live?.error || task.error) && (
        <DetailRow label="Error"><span className="form-error">{live?.error ?? task.error?.message}</span></DetailRow>
      )}

      {/* ST42 — the four phase timestamps + per-phase durations. */}
      <div>
        <h4>Timeline</h4>
        {phases.length === 0 ? (
          <span className="muted small">No phase timestamps recorded.</span>
        ) : (
          phases.map((p) => (
            <DetailRow key={p.label} label={p.label}>{new Date(p.ts).toLocaleString()}</DetailRow>
          ))
        )}
        <div className="muted-row small" style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 4 }}>
          <span>queue {fmtDur(durations.queue)}</span>
          <span>dispatch {fmtDur(durations.dispatch)}</span>
          <span>run {fmtDur(durations.run)}</span>
          <span>total {fmtDur(durations.total)}</span>
        </div>
      </div>

      <div>
        <h4>Input / arguments</h4>
        {args ? (
          <JsonView value={args} />
        ) : fullLoading && task.arguments_omitted ? (
          <Loading />
        ) : argStr ? (
          <DetailRow label="Args ref"><code className="mono small">{argStr}</code></DetailRow>
        ) : (
          <span className="muted small">No input arguments recorded for this task.</span>
        )}
      </div>

      {/* ST42 — live result: task.completed populates this WITHOUT a refetch. Otherwise the result
          comes from the FULL row (GET /tasks/{id}); the light list row only says whether one exists. */}
      {(live?.result != null || (live?.output_refs?.length ?? 0) > 0 || resultRef != null || task.result_omitted) && (
        <div>
          <h4>Result</h4>
          {live?.result != null ? (
            <JsonView value={live.result} />
          ) : resultRef != null ? (
            <JsonView value={resultRef} />
          ) : fullLoading ? (
            <span className="muted small" data-testid="task-result-loading">
              Loading result{task.result_bytes != null ? ` (~${fmtBytes(task.result_bytes)})` : ''}…
            </span>
          ) : (
            <span className="muted small">
              Result not available{task.result_bytes != null ? ` (~${fmtBytes(task.result_bytes)})` : ''}.
            </span>
          )}
          {(live?.output_refs?.length ?? 0) > 0 && (
            <DetailRow label="Output refs">
              <span className="client-meta">{live!.output_refs!.map((r) => <code key={r} className="mono small">{r}</code>)}</span>
            </DetailRow>
          )}
        </div>
      )}

      {/* ST42 — log tail if the gateway stores it, else a link to the owning node. */}
      <div>
        <h4>Logs</h4>
        {task.log_tail?.length ? (
          <pre className="json-view result-text">{task.log_tail.join('\n')}</pre>
        ) : task.client_id ? (
          <Link to={`/clients`} className="muted small">View node {task.client_id} →</Link>
        ) : (
          <span className="muted small">No log tail available.</span>
        )}
      </div>
      {confirm && (
        <ConfirmDialog
          title="Cancel task"
          message={<>Cancel this <code className="mono small">{task.canonical_tool}</code> task?</>}
          confirmLabel="Cancel task"
          busy={busy}
          error={err}
          onConfirm={cancel}
          onClose={() => setConfirm(false)}
        />
      )}
    </Drawer>
  );
}

